import { SchmancyTheme } from './theme.interface'

/**
 * The tone axis — the one semantic-color vocabulary every tonal mark in the
 * library resolves through: `<schmancy-badge>` (the label mark),
 * `<schmancy-tone-dot>` (the point mark), `<schmancy-tone-spine>` (the edge
 * mark), `<schmancy-assist-chip>` (the action mark).
 *
 * Sibling of `control-size.ts`: that file is the one size ladder, this is the
 * one color ladder. A component picks its geometry and its interactivity; the
 * tone it paints in is never re-decided per component.
 */
export const TONES = [
	'primary',
	'secondary',
	'tertiary',
	'success',
	'warning',
	'info',
	'error',
	'neutral',
	'surface',
] as const

export type SchmancyTone = (typeof TONES)[number]

/**
 * The six slots a tonal mark ever needs, in three pairs. `ink`/`onInk` is the
 * saturated pair — a filled status dot, a solid identity spine, an outline;
 * `pinned`/`onPinned` is the scheme-stable filled pair — the ground a large
 * tone-filled area reporting a state stands on; `fill`/`onFill` is the tinted
 * pair — a soft pill, a tinted icon disc, a badge.
 *
 * `ink` flips by scheme (`error: isDark ? 80 : 40`) because its job is to be
 * READ against whatever surface is behind it — a light tone so a small mark
 * carries on a dark ground. `pinned` does not flip, because its job is to BE
 * the ground, and a fill inverted with the scheme turns a destructive confirm
 * into a soft notice. `theme.format.ts` is the one home for that pair's four
 * measurements; this points at them rather than restating them.
 *
 * Only the four state roles (`error`, `success`, `warning`, `info`) carry a
 * pinned pair of their own. Every other role aliases `ink`/`onInk` below, so
 * the `pinned` rung degrades to `solid` for them rather than breaking.
 *
 * Every value is a `var(--schmancy-sys-color-*)` reference, so a mark applies
 * it with the `color()` directive and follows the ambient theme with no
 * per-component color decision. Nothing here is a literal.
 */
export interface ToneRoles {
	/** Saturated ground or hairline. Flips by scheme. */
	ink: string
	/** Legible ink on `ink`. */
	onInk: string
	/** Filled ground — one value in both schemes. Aliases `ink` where the role carries no pair of its own. */
	pinned: string
	/** Legible ink on `pinned`. */
	onPinned: string
	/** Tinted carrier. */
	fill: string
	/** Legible ink on `fill`. */
	onFill: string
}

/**
 * `neutral` and `surface` carry no hue of their own — they are the two
 * un-toned rungs, and they differ in how loudly they paint. `surface` is the
 * page's own pair: `ink` is `surface.on` over an `onInk` of `surface.default`,
 * the maximum-contrast rung for a mark that has to be read the way body text
 * is. `neutral` is the RECESSIVE rung: its `ink` is `surface.onVariant`, the
 * ink the theme guarantees legible over the surface while sitting back from
 * it — because `neutral` is what a mark wears when nothing is wrong, and a
 * mark painted at the page's own maximum contrast outshouts every toned mark
 * beside it. A 10 px `neutral` segment across a whole bar is the case that
 * proves it: at `surface.on` the cohort that merely left is the loudest thing
 * on the surface. Contrast is symmetric in both rungs, so the pair the theme
 * guarantees legible one way round is legible the other, and `onInk` stays
 * `surface.default` for both.
 */
export const TONE: Record<SchmancyTone, ToneRoles> = {
	primary: {
		ink: SchmancyTheme.sys.color.primary.default,
		onInk: SchmancyTheme.sys.color.primary.on,
		pinned: SchmancyTheme.sys.color.primary.default,
		onPinned: SchmancyTheme.sys.color.primary.on,
		fill: SchmancyTheme.sys.color.primary.container,
		onFill: SchmancyTheme.sys.color.primary.onContainer,
	},
	secondary: {
		ink: SchmancyTheme.sys.color.secondary.default,
		onInk: SchmancyTheme.sys.color.secondary.on,
		pinned: SchmancyTheme.sys.color.secondary.default,
		onPinned: SchmancyTheme.sys.color.secondary.on,
		fill: SchmancyTheme.sys.color.secondary.container,
		onFill: SchmancyTheme.sys.color.secondary.onContainer,
	},
	tertiary: {
		ink: SchmancyTheme.sys.color.tertiary.default,
		onInk: SchmancyTheme.sys.color.tertiary.on,
		pinned: SchmancyTheme.sys.color.tertiary.default,
		onPinned: SchmancyTheme.sys.color.tertiary.on,
		fill: SchmancyTheme.sys.color.tertiary.container,
		onFill: SchmancyTheme.sys.color.tertiary.onContainer,
	},
	success: {
		ink: SchmancyTheme.sys.color.success.default,
		onInk: SchmancyTheme.sys.color.success.on,
		pinned: SchmancyTheme.sys.color.success.pinned,
		onPinned: SchmancyTheme.sys.color.success.onPinned,
		fill: SchmancyTheme.sys.color.success.container,
		onFill: SchmancyTheme.sys.color.success.onContainer,
	},
	warning: {
		ink: SchmancyTheme.sys.color.warning.default,
		onInk: SchmancyTheme.sys.color.warning.on,
		pinned: SchmancyTheme.sys.color.warning.pinned,
		onPinned: SchmancyTheme.sys.color.warning.onPinned,
		fill: SchmancyTheme.sys.color.warning.container,
		onFill: SchmancyTheme.sys.color.warning.onContainer,
	},
	info: {
		ink: SchmancyTheme.sys.color.info.default,
		onInk: SchmancyTheme.sys.color.info.on,
		pinned: SchmancyTheme.sys.color.info.pinned,
		onPinned: SchmancyTheme.sys.color.info.onPinned,
		fill: SchmancyTheme.sys.color.info.container,
		onFill: SchmancyTheme.sys.color.info.onContainer,
	},
	error: {
		ink: SchmancyTheme.sys.color.error.default,
		onInk: SchmancyTheme.sys.color.error.on,
		pinned: SchmancyTheme.sys.color.error.pinned,
		onPinned: SchmancyTheme.sys.color.error.onPinned,
		fill: SchmancyTheme.sys.color.error.container,
		onFill: SchmancyTheme.sys.color.error.onContainer,
	},
	neutral: {
		ink: SchmancyTheme.sys.color.surface.onVariant,
		onInk: SchmancyTheme.sys.color.surface.default,
		pinned: SchmancyTheme.sys.color.surface.onVariant,
		onPinned: SchmancyTheme.sys.color.surface.default,
		fill: SchmancyTheme.sys.color.surface.containerHigh,
		onFill: SchmancyTheme.sys.color.surface.on,
	},
	surface: {
		ink: SchmancyTheme.sys.color.surface.on,
		onInk: SchmancyTheme.sys.color.surface.default,
		pinned: SchmancyTheme.sys.color.surface.on,
		onPinned: SchmancyTheme.sys.color.surface.default,
		fill: SchmancyTheme.sys.color.surface.container,
		onFill: SchmancyTheme.sys.color.surface.on,
	},
}

/**
 * How hard a mark presses its tone — a five-rung ladder from full to none,
 * meaning the same thing on every member of the family:
 *
 * - `solid` — the saturated `ink` ground. The only rung that survives at 8px,
 *   so it is the status dot's default.
 * - `pinned` — the scheme-stable `pinned` ground, bounded by a hairline in the
 *   role's own `ink`. `solid` for a small MARK, `pinned` for a filled AREA
 *   reporting a state: `solid`'s ground flips with the scheme because that flip
 *   serves `ink`'s reading job, and a 60px rail inverted with it reports a
 *   failure in pale pink over dark red. The hairline is the boundary that
 *   ground owes on a dark surface, where it falls under the non-text floor on
 *   its own while the flipped `ink` clears it — `theme.format.ts` holds those
 *   four measurements. In light the two tones are one value, so the hairline is
 *   a no-op exactly where the ground already carries its own edge. A role with
 *   no pinned pair renders this rung as `solid`, hairline included: an edge in
 *   the ground's own colour bounds nothing.
 * - `soft` — the tinted `fill` ground. The carrier a glyph or a label sits on
 *   without shouting.
 * - `outline` — the tone at the edge only: a hairline and ink in `ink`, over
 *   whatever ground the host already painted. This is the rung a stamp, a
 *   ticket tile, or an outlined badge sits on — present and toned, but not
 *   competing with the surface it annotates.
 * - `hollow` — no tone at all: the third state of a tri-state (offline,
 *   unknown, not-yet).
 *
 * `outline` leaves the ground transparent and `hollow` paints the page ground
 * without a boundary, for the same reason in both cases: what sits behind the
 * mark is the host's decision, and a mark that guessed it would fight the very
 * surface it was dropped onto.
 */
export const TONE_VARIANTS = ['solid', 'pinned', 'soft', 'outline', 'hollow'] as const

export type ToneVariant = (typeof TONE_VARIANTS)[number]

/**
 * The ground, ink, and edge a (tone, variant) renders — keyed as CSS property
 * names so it goes straight into `styleMap`. An inline style is what the whole
 * family writes: every value here is a `var(--schmancy-sys-color-*)` reference
 * Tailwind cannot spell as one static class across nine tones × five rungs,
 * and `styleMap` drops the declarations it stops emitting, so a mark that
 * loses its tone loses the paint with it.
 *
 * `borderColor` names an edge's COLOUR and nothing else — the width and style
 * are the mark's own — so a component that renders a rung the map gave a
 * hairline carries a `border border-solid` of its own or draws no edge at all.
 */
export function toneColors(
	color: SchmancyMarkColor,
	variant: ToneVariant,
): { backgroundColor: string; color: string; borderColor: string | undefined } {
	const roles = markRoles(color)
	if (variant === 'solid') return { backgroundColor: roles.ink, color: roles.onInk, borderColor: undefined }
	if (variant === 'pinned')
		return {
			backgroundColor: roles.pinned,
			color: roles.onPinned,
			// A role with no pinned pair aliases `ink`, so this asks whether the role
			// carries one at all — where it does not the ground IS the hairline's
			// colour, and the rung is `solid` down to the last declaration.
			borderColor: roles.pinned === roles.ink ? undefined : roles.ink,
		}
	if (variant === 'soft') return { backgroundColor: roles.fill, color: roles.onFill, borderColor: undefined }
	if (variant === 'outline') return { backgroundColor: 'transparent', color: roles.ink, borderColor: roles.ink }
	return {
		backgroundColor: SchmancyTheme.sys.color.surface.default,
		color: SchmancyTheme.sys.color.surface.onVariant,
		borderColor: undefined,
	}
}

/**
 * The peer rotation — the theme's six identity tints in index order, the
 * channel for "which one is this" (which person, which event, which retrieval
 * channel). Not part of the tone axis above and deliberately kept beside it:
 * the tones are a ladder of MEANING (error, success, neutral) where the
 * rotation is a ladder of NOTHING but hue, one tone and one chroma across six
 * evenly spaced hues, so every member carries the same visual weight and no
 * member can be read as a state.
 *
 * Reach for this wherever an ENTITY needs a colour of its own — an avatar's
 * initials disc, a tag, a mention — rather than hand-picking a subset of the
 * semantic roles, which M3 gives differing chroma so some members shout and
 * others vanish however contrast-correct each is alone. A chart mark takes
 * `SERIES` below instead: these tints are tinted carriers whose onContainer ink
 * carries the identity, and stripped of that ink they sit 4.2 OKLab ΔE apart.
 */
export const IDENTITY_TINTS = Object.values(SchmancyTheme.sys.color.identity)

/** The rotation's member for `index`, wrapping — the caller passes a series
 *  position or a hash and never has to know how many tints exist. */
export function identityTint(index: number): (typeof IDENTITY_TINTS)[number] {
	return IDENTITY_TINTS[Math.abs(Math.trunc(index)) % IDENTITY_TINTS.length]
}

/**
 * The categorical chart scale — one colour per data series, in the fixed order
 * `theme.format.ts` measured them in. A series index is the second thing a mark
 * can be coloured by, beside the tone axis above, and the two are disjoint on
 * purpose: a tone says what a mark MEANS, a series index says only which member
 * of a set it is.
 */
export const SERIES = Object.values(SchmancyTheme.sys.color.series)

/** How many members the categorical scale has. A chart with more series than
 *  this has more series than the gate can separate, so the overflow is folded
 *  into a named rest ("other", "übrige") BEFORE it reaches a colour — never
 *  handed to `seriesColor`, which would repaint it as a member it is not. */
export const SERIES_STEPS = SERIES.length

/** The scale's member for `index`. The index folds into `SERIES_STEPS`, so a
 *  caller may hand over a hash or a position without knowing the count — but a
 *  folded index paints a colour some other member already has, which is why a
 *  chart folds its OWN overflow into one rest member first (`SERIES_STEPS`
 *  above) rather than letting this wrap silently. A negative index folds from
 *  the end rather than mirroring onto its positive twin, so −1 is the last
 *  step and never step 1. */
export function seriesColor(index: number): (typeof SERIES)[number] {
	const step = Math.trunc(index) % SERIES_STEPS
	return SERIES[(step + SERIES_STEPS) % SERIES_STEPS]
}

/**
 * What a tonal mark can be coloured by: a tone, or a categorical series index.
 * A number reaches this type from an attribute as its own decimal string, which
 * `markRoles` reads back — so `color="3"` and `.color=${3}` are one value.
 */
export type SchmancyMarkColor = SchmancyTone | number

/**
 * A series index reaching this from an attribute is a NON-NEGATIVE decimal
 * string and nothing else. `Number()` is not that test: it reads `''`, `' '`
 * and `null` as a finite `0` and `'0x10'` as `16`, so an unset or blank
 * `color=` on a reflecting attribute would select a series step in place of
 * the tone the mark documents as its default. A negative number is not a
 * position in the scale either, so it lands on that same documented default
 * rather than on whichever step its magnitude happens to name.
 */
const SERIES_INDEX = /^\s*\d+(\.\d+)?\s*$/

/**
 * The six slots for either kind of colour. A series step has one weight rather
 * than a tonal family — it is a single measured colour, and tinting it would
 * walk it straight back out of the band it was measured inside — so all three
 * of its pairs are the same pair, and a `soft` or `pinned` series mark reads
 * exactly as a `solid` one, hairline included.
 *
 * A value that is not a position in the scale — a negative number, a
 * non-finite one, or anything that is not a member of the tone axis — lands on
 * `primary`: every mark in the family already documents that as its default,
 * and a typo'd attribute has to render something rather than throw inside a
 * consumer's template.
 */
export function markRoles(color: SchmancyMarkColor): ToneRoles {
	const index = typeof color === 'number' ? color : SERIES_INDEX.test(String(color)) ? Number(color) : Number.NaN
	if (!Number.isFinite(index) || index < 0) return TONE[color as SchmancyTone] ?? TONE.primary
	const series = seriesColor(index)
	return {
		ink: series.default,
		onInk: series.on,
		pinned: series.default,
		onPinned: series.on,
		fill: series.default,
		onFill: series.on,
	}
}
